import React, { createContext, useContext, useMemo, useState } from "react";
import { ThemeProvider } from "@mui/material/styles";
import { Theme } from "@mui/material/styles";
import { BrandConfig, ThemeMode } from "./types";
import { createBrandTheme } from "./createBrandTheme";

interface BrandContextValue {
  brand: BrandConfig;
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  theme: Theme;
}

const BrandContext = createContext<BrandContextValue | undefined>(undefined);

interface BrandProviderProps {
  brand: BrandConfig;
  children: React.ReactNode;
}

// Aplica o tema da marca via MUI ThemeProvider e expõe brand/mode/setMode.
// Tema memoizado por brand.id+mode — não recria a cada render (anti-padrão evitado).
export const BrandProvider: React.FC<BrandProviderProps> = ({ brand, children }) => {
  const [mode, setMode] = useState<ThemeMode>(brand.defaultMode ?? "light");

  const theme = useMemo(() => createBrandTheme(brand, mode), [brand, mode]);

  const value = useMemo(
    () => ({ brand, mode, setMode, theme }),
    [brand, mode, theme]
  );

  return (
    <BrandContext.Provider value={value}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </BrandContext.Provider>
  );
};

export const useBrand = (): BrandContextValue => {
  const ctx = useContext(BrandContext);
  if (!ctx) {
    throw new Error("useBrand deve ser utilizado dentro de um BrandProvider");
  }
  return ctx;
};
